import { useState, useEffect } from "preact/hooks";
import { strings } from "../i18n/strings.es-AR.js";
import { conversation } from "../state/store.js";
import { getEntry } from "../hooks/useConversation.js";
import {
  isPushSupported, getNotificationPermission, shouldShowPushBanner, enablePush, prefetchPushConfig, isPushBlocked
} from "../push/push.js";
import { getPushPromptDismissed, setPushPromptDismissed } from "../state/persistence.js";
import { track } from "../metrics/track.js";

// Ofrece activar Web Push cuando el handoff a humano se activa: es el momento
// en que las respuestas llegan de forma asíncrona y el aviso tiene valor.
//
// Es la ÚNICA superficie de avisos del webchat: una vez otorgado el permiso el
// banner desaparece y el chat no vuelve a hablar del tema (ni confirmación ni
// opción de desactivar). La baja se hace desde los permisos del navegador, que
// además limpia solo: al revocar, el endpoint queda muerto y el server borra la
// suscripción con el 404/410 (ver services/pushNotifier.js).
export function NotificationsBanner() {
  const [dismissed, setDismissed] = useState(getPushPromptDismissed());
  const [permission, setPermission] = useState(getNotificationPermission());
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // El navegador ya avisó que no da el permiso (ver isPushBlocked).
  const [blocked, setBlocked] = useState(false);

  const supported = isPushSupported(getEntry());
  const handoffActive = conversation.value.handoffActive;

  // Prefetch del config VAPID apenas el banner tiene sentido: así el click de
  // "Avisarme" puede llamar requestPermission() sin un round-trip previo que
  // consuma el gesto del usuario (Safari/iOS, Firefox).
  useEffect(() => {
    if (!supported || !handoffActive) return undefined;
    prefetchPushConfig().catch(() => {});
    let active = true;
    isPushBlocked().then((value) => { if (active) setBlocked(value); });
    return () => { active = false; };
  }, [supported, handoffActive]);

  // Si el navegador no va a dar el permiso (incógnito, o el usuario los
  // bloqueó), en vez de ofrecer "Avisarme" se aclara una vez por qué no hay
  // avisos y que la respuesta igual aparece en el chat.
  const showBlockedNotice = supported && handoffActive && !dismissed && (blocked || permission === "denied");
  // Con la explicación de un rechazo a la vista, el banner queda aunque el
  // permiso ya diga "denied": si no, desaparecía sin decir por qué.
  if (!feedback && !showBlockedNotice && !shouldShowPushBanner({ supported, permission, dismissed, handoffActive })) return null;
  const notice = feedback || (showBlockedNotice ? strings.pushDenied : null);

  const onEnable = async () => {
    setBusy(true);
    setFeedback(null);
    const result = await enablePush(conversation.value.id);
    setBusy(false);
    setPermission(getNotificationPermission());
    track(result === "granted" ? "push_enabled" : "push_declined", { result });
    // Feedback explícito en los casos que antes se tragaban. Un rechazo no se
    // vuelve a ofrecer: en incógnito, por ejemplo, va a fallar siempre.
    if (result === "denied") {
      setFeedback(strings.pushDenied);
      setPushPromptDismissed();
    } else if (result === "error" || result === "unsupported") {
      setFeedback(strings.pushError);
    }
  };

  const onDismiss = () => {
    setPushPromptDismissed();
    setDismissed(true);
    track("push_prompt_dismissed", {});
  };

  return (
    <div class="bg-flux-navy-mid px-4 py-2 text-xs text-white">
      <div class="chat-col flex items-center gap-2">
        <span class="min-w-0 flex-1">{notice || strings.pushBannerText}</span>
        {notice === strings.pushDenied ? (
          // Bloqueado o rechazado: no hay nada que reintentar, sólo cerrar la
          // explicación (y no volver a mostrarla).
          <button
            type="button"
            class="shrink-0 rounded-full bg-flux-cyan px-3 py-1 font-medium text-flux-navy"
            onClick={() => { setFeedback(null); setPushPromptDismissed(); setDismissed(true); }}
          >
            {strings.pushFeedbackOk}
          </button>
        ) : (
          <>
            <button
              type="button"
              class="shrink-0 rounded-full bg-flux-cyan px-3 py-1 font-medium text-flux-navy disabled:opacity-40"
              disabled={busy}
              onClick={onEnable}
            >
              {strings.pushEnable}
            </button>
            <button
              type="button"
              class="shrink-0 rounded-full px-2 py-1 text-white/70"
              onClick={onDismiss}
            >
              {strings.pushDismiss}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
